/**
 * サイト内行動の dataLayer プッシュ（GA4 / GTM 用）
 *
 * GA4 の推奨イベントと同名になるもののうち、view_item だけは標準名で送る（view_item_list / select_item と揃える）。
 * それ以外はすべて fs_ を付ける（GTM 側で GA4 のイベント名に読み替える）。
 * 商品一覧（view_item_list / select_item）は component-datalayer-list.js、
 * カート系（fs_add_to_cart 等）は component-datalayer-cart.js が担当。
 *
 * [商品詳細]
 *   view_item, fs_variant_select, fs_size_chart_open, fs_product_tab_open, fs_store_availability_open,
 *   fs_quick_buy_open, fs_share_click, fs_add_to_wishlist / fs_remove_from_wishlist, fs_back_in_stock_open,
 *   fs_add_to_cart_error（在庫切れ・購入制限などでカート追加に失敗）
 * [プロモーション]
 *   fs_view_promotion / fs_select_promotion : スライドショー・カテゴリ導線・コレクション一覧・カスタムブロック・
 *     LP バナー・コレクション内プロモ枠。[data-promotion] / [data-promotion-slide] による明示指定にも対応
 * [検索]
 *   fs_search（検索語は送らない。文字数のみ）, fs_view_search_results, fs_predictive_search_open, fs_predictive_search_click
 * [回遊]
 *   fs_menu_click, fs_menu_open, fs_filter_open, fs_collection_filter, fs_collection_sort, fs_layout_switch,
 *   fs_pagination_click, fs_breadcrumb_click, fs_social_click, fs_cta_click, fs_outbound_click, fs_contact_link_click,
 *   fs_accordion_open（FAQ 等）, fs_video_play, fs_scroll_depth
 * [会員・リード]
 *   fs_login / fs_sign_up（送信）, fs_login_success / fs_sign_up_success（次ページでログイン済みなら）,
 *   fs_password_recover, fs_newsletter_signup / fs_newsletter_signup_success, fs_contact_submit / fs_contact_success,
 *   fs_popup_view / fs_popup_close
 */
(() => {
  window.dataLayer = window.dataLayer || [];
  const page = window.fsPageData || {};

  const push = (event, params = {}) => {
    window.dataLayer.push({ event, ...params });
  };
  const text = (el) => (el ? (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '');
  const href = (a) => {
    try {
      return new URL(a.getAttribute('href'), location.href).href;
    } catch (_) {
      return a.getAttribute('href') || '';
    }
  };
  const on = (type, selector, handler, opts = true) => {
    document.addEventListener(
      type,
      (e) => {
        const target = e.target instanceof Element ? e.target : null;
        const el = target && target.closest(selector);
        if (el) handler(el, e, target);
      },
      opts
    );
  };
  const onceSet = new WeakSet();
  const once = (el) => {
    if (onceSet.has(el)) return false;
    onceSet.add(el);
    return true;
  };
  const sectionKey = (el) => {
    const section = el instanceof Element ? el.closest('.shopify-section') : null;
    return section ? section.id.replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '') : 'unknown';
  };
  const listAttr = (productId) => (typeof window.fsListAttr === 'function' ? window.fsListAttr(productId) : {});

  // 商品情報（カード内ならカードの data 属性、それ以外は商品ページの fsPageData から）
  const productParams = (el) => {
    const card = el instanceof Element ? el.closest('[data-list-item]') : null;
    if (card) {
      return { item_id: card.dataset.itemId, item_name: card.dataset.itemName, item_variant: card.dataset.itemVariant, price: Number(card.dataset.price) };
    }
    const p = page.product;
    return p ? { item_id: p.item_id, item_name: p.item_name, item_variant: p.item_variant, price: p.price } : {};
  };

  // ---- view_item -------------------------------------------------------------
  // 直前にクリックした一覧の情報（item_list_id / item_list_name / index）があれば items に足す。
  // window.fsListAttr は component-datalayer-list.js（defer）で定義されるため、DOMContentLoaded 後に送る。
  if (page.product) {
    const pushViewItem = () => {
      const item = { ...page.product, ...listAttr(page.product.item_id) };
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: { currency: page.currency, value: item.price, items: [item] },
      });
      // 旧 external_tag.liquid の gtag view_item（Google 広告の動的リマーケティング用）を互換のため残す。
      // 以前は gtag 未定義だと ReferenceError になっていたため、定義されているときだけ送る。
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_item', {
          value: String(Math.ceil(item.price * 1.1)),
          items: [{ id: item.item_variant, google_business_vertical: 'retail' }],
        });
      }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pushViewItem);
    else pushViewItem();
  }

  // ---- 商品詳細 --------------------------------------------------------------
  document.addEventListener('variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    const prev = e.detail && e.detail.previousVariant;
    if (!v || (prev && prev.id === v.id) || !prev) return; // 初期化時（previousVariant なし）は送らない
    const inQuickBuy = e.target instanceof Element && !!e.target.closest('quick-buy-modal');
    // 商品ページのフォームなら、以降のイベント（今すぐ購入など）用に現在のバリアントを反映する
    if (page.product && !inQuickBuy && e.detail.product && String(e.detail.product.id) === page.product.item_id) {
      Object.assign(page.product, {
        item_variant: String(v.id),
        variant_title: v.title,
        sku: v.sku,
        price: v.price / 100,
        item_available: !!v.available,
      });
    }
    push('fs_variant_select', {
      item_id: e.detail.product ? String(e.detail.product.id) : productParams(e.target).item_id,
      item_name: e.detail.product ? e.detail.product.title : undefined,
      item_variant: String(v.id),
      variant_title: v.title,
      variant_available: !!v.available,
      price: typeof v.price === 'number' ? v.price / 100 : undefined,
      variant_context: inQuickBuy ? 'quick_buy' : 'product_page',
    });
  });

  document.addEventListener('cart:error', (e) => {
    push('fs_add_to_cart_error', {
      ...productParams(e.target),
      error_message: String((e.detail && e.detail.error) || '').slice(0, 100),
    });
  });

  on('click', '.share-buttons a, [is="share-button"]', (el) =>
    push('fs_share_click', { ...productParams(el), method: el.getAttribute('aria-label') || text(el) || 'native' })
  );

  // Wishlist King（アプリのボタン）
  on('click', '[data-wk-add-product], [data-wk-remove-product], wishlist-button-product, wishlist-button-collection, .wk-button, [class*="wishlist-button"]', (el) => {
    const removing = el.hasAttribute('data-wk-remove-product') || el.classList.contains('wk-selected') || el.getAttribute('aria-pressed') === 'true' || !!el.querySelector('.wk-selected');
    push(removing ? 'fs_remove_from_wishlist' : 'fs_add_to_wishlist', productParams(el));
  });

  // 再入荷通知 / 予約通知アプリ
  on('click', '[id*="notify-me"], [class*="notify-me"], [class*="back-in-stock"], [id*="BIS_trigger"]', (el) => push('fs_back_in_stock_open', productParams(el)));

  // アコーディオン（商品詳細のタブ / FAQ 等）。toggle はバブリングしないためキャプチャで拾う
  document.addEventListener(
    'toggle',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLDetailsElement) || !el.open || !el.matches('.accordion')) return;
      const name = text(el.querySelector('summary'));
      if (el.closest('.product-info, .product')) push('fs_product_tab_open', { ...productParams(el), tab_name: name });
      else push('fs_accordion_open', { accordion_name: name, accordion_section: sectionKey(el), page_handle: page.page_handle || '' });
    },
    true
  );

  // 動画再生（商品メディア・動画セクション）
  ['media:play', 'player:start'].forEach((name) => {
    document.addEventListener(
      name,
      (e) => {
        const el = e.target instanceof Element ? e.target : null;
        if (!el || !once(el)) return;
        push('fs_video_play', {
          ...(el.closest('product-gallery, .product') ? productParams(el) : {}),
          video_location: el.closest('product-gallery') ? 'product_media' : sectionKey(el),
          media_type: el.tagName.toLowerCase(),
        });
      },
      true
    );
  });

  // ---- ダイアログ（ドロワー・モーダル・ポップアップ）------------------------------
  // dialog:after-show / after-hide はバブリングしないためキャプチャで拾う。カートドロワーは component-datalayer-cart.js 側
  const POPUP_TAGS = 'newsletter-popup, pop-in, x-modal, x-drawer';
  const popupName = (el) => el.dataset.popupName || el.id || el.tagName.toLowerCase();
  document.addEventListener(
    'dialog:after-show',
    (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (!el || el.matches('cart-drawer, privacy-banner, x-popover, facets-sort-popover')) return;
      if (el.matches('header-search')) return push('fs_predictive_search_open');
      if (el.matches('header-sidebar')) return push('fs_menu_open', { menu_location: 'sidebar' });
      if (el.matches('facets-drawer')) return push('fs_filter_open', { list_page_type: page.page_type });
      if (el.matches('quick-buy-modal')) return push('fs_quick_buy_open', productParams(el));
      if (el.closest('pickup-availability')) return push('fs_store_availability_open', productParams(el));
      if (el.closest('variant-picker') && el.matches('x-modal')) return push('fs_size_chart_open', productParams(el));
      if (el.matches(POPUP_TAGS)) push('fs_popup_view', { popup_name: popupName(el) });
    },
    true
  );
  document.addEventListener(
    'dialog:after-hide',
    (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el && el.matches('newsletter-popup, pop-in')) push('fs_popup_close', { popup_name: popupName(el) });
    },
    true
  );

  // ---- プロモーション --------------------------------------------------------
  // [selector, promotion の種類]。どのセクションのどの枠かは .shopify-section の id から組み立てる
  const PROMOS = [
    ['[data-promotion-slide]', 'custom'],
    ['slideshow-carousel .slideshow__slide', 'slideshow'],
    ['a[class*="Featured--linksItem"]', 'featured_links'],
    ['a.collection-card', 'collection_list'],
    ['a.custom-block-link', 'custom_blocks'],
    ['a.lp-banner-carousel__card', 'lp_banner'],
    ['.product-card-promo a', 'collection_promo'],
  ];
  const PROMO_SELECTOR = PROMOS.map(([s]) => s).join(', ');
  const promoOf = (el) => {
    const hit = PROMOS.find(([s]) => el.matches(s));
    if (!hit) return null;
    const container = el.closest('[data-promotion]') || el.closest('.shopify-section') || document.body;
    const key = sectionKey(el);
    const siblings = [...container.querySelectorAll(hit[0])];
    const img = el.querySelector('img[alt]');
    return {
      promotion_id: container.dataset.promotionId || `${hit[1]}_${key}`,
      promotion_name: container.dataset.promotionName || `${hit[1]}:${key}`,
      creative_name: (
        el.dataset.creativeName ||
        text(el.querySelector('.slideshow__slide-content .h1, .slideshow__slide-content .heading, .LinkTitle, .collection-card__title, .h1, .h2, .h3, .h4, .h5, .h6')) ||
        (img && img.getAttribute('alt')) ||
        text(el)
      ).slice(0, 100),
      creative_slot: el.dataset.creativeSlot || String(siblings.indexOf(el) + 1),
      promotion_page_type: page.page_type,
    };
  };

  // スライドショーの非選択スライドは重なって配置されているため、選択中のものだけ表示扱いにする
  const isActive = (el) => !el.matches('.slideshow__slide') || el.classList.contains('is-selected');
  const inView = new Set();
  const tryView = (el) => {
    if (!inView.has(el) || !isActive(el) || !el.getClientRects().length || !once(el)) return;
    const params = promoOf(el);
    if (params) push('fs_view_promotion', params);
  };
  const promoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                inView.add(entry.target);
                tryView(entry.target);
              } else {
                inView.delete(entry.target);
              }
            });
          },
          { threshold: 0.5 }
        )
      : null;
  const observed = new WeakSet();
  const observePromotions = () => {
    if (!promoObserver) return;
    document.querySelectorAll(PROMO_SELECTOR).forEach((el) => {
      if (observed.has(el)) return;
      observed.add(el);
      promoObserver.observe(el);
    });
  };
  ['carousel:change', 'carousel:select', 'carousel:settle'].forEach((name) =>
    document.addEventListener(name, () => setTimeout(() => inView.forEach(tryView), 100), true)
  );
  on('click', PROMO_SELECTOR, (el, e, target) => {
    const params = promoOf(el);
    if (!params) return;
    const a = target.closest('a[href]') || (el.matches('a[href]') ? el : el.querySelector('a[href]'));
    push('fs_select_promotion', { ...params, link_url: a ? href(a) : '', link_text: a ? text(a) : '' });
  });

  // ---- 検索 ------------------------------------------------------------------
  on('submit', 'form[role="search"], form[action*="/search"]', (form) => {
    const input = form.querySelector('[name="q"]');
    push('fs_search', {
      search_term_length: input ? input.value.trim().length : 0,
      search_location: form.closest('header-search') ? 'header' : 'page',
    });
  });
  if (page.page_type === 'search') {
    push('fs_view_search_results', {
      search_performed: !!page.search_performed,
      search_results_count: page.search_results_count || 0,
      search_zero_results: !!page.search_performed && !page.search_results_count,
      search_term_length: page.search_term_length || 0,
    });
  }
  on('click', 'predictive-search a[href]', (a) => {
    const resultType = a.closest('[data-list-item]')
      ? 'product'
      : /\/collections\//.test(a.pathname)
      ? 'collection'
      : /\/blogs\//.test(a.pathname)
      ? 'article'
      : /\/pages\//.test(a.pathname)
      ? 'page'
      : /\/search/.test(a.pathname)
      ? 'query'
      : 'other';
    push('fs_predictive_search_click', { ...productParams(a), link_url: href(a), link_text: text(a), result_type: resultType });
  });

  // ---- 回遊 ------------------------------------------------------------------
  const MENUS = [
    ['.mega-menu-gear', 'mega_menu_gear'],
    ['.mega-menu', 'mega_menu'],
    ['.header__dropdown-menu, dropdown-menu-disclosure', 'dropdown_menu'],
    ['.header__primary-nav', 'header'],
    ['header-sidebar', 'sidebar'],
    ['.header__secondary-nav', 'header_icon'],
    ['.announcement-bar', 'announcement_bar'],
    ['.footer', 'footer'],
  ];
  const EXCLUDE = '[data-list-item], .share-buttons, .social-media, .breadcrumb, .pagination, predictive-search, header-search, cart-drawer';
  on('click', 'a[href]', (a) => {
    if (a.closest(EXCLUDE) || a.matches(PROMO_SELECTOR) || a.closest(PROMO_SELECTOR)) return;
    const hit = MENUS.find(([sel]) => a.closest(sel));
    if (hit) {
      push('fs_menu_click', { menu_location: hit[1], link_text: text(a) || a.getAttribute('aria-label') || '', link_url: href(a) });
      return;
    }
    // メニュー以外のボタン型リンク（LP・特集の CTA）
    if (a.matches('.button, [class*="button"], [class*="btn"], [class*="cta"]') && a.closest('main, #main')) {
      push('fs_cta_click', { link_text: text(a) || a.getAttribute('aria-label') || '', link_url: href(a), cta_section: sectionKey(a), page_handle: page.page_handle || '' });
    }
  });

  on('change', 'form[is="facets-form"] input, form[is="facets-form"] select', (input) => {
    if (input.type === 'hidden') return;
    push('fs_collection_filter', {
      filter_name: input.name,
      filter_value: String(input.value).slice(0, 100),
      filter_checked: input.type === 'checkbox' || input.type === 'radio' ? input.checked : true,
      list_page_type: page.page_type,
    });
  });
  document.addEventListener('listbox:change', (e) => {
    const el = e.target instanceof Element ? e.target : null;
    if (!el || !el.closest('facets-sort-popover, [id*="sort"]')) return;
    push('fs_collection_sort', { sort_value: (e.detail && e.detail.value) || '', list_page_type: page.page_type });
  });
  on('click', 'collection-layout-switch button', (btn) =>
    push('fs_layout_switch', { layout_value: btn.value || btn.getAttribute('aria-label') || text(btn) })
  );
  on('click', '.pagination a.pagination__link[href]', (a) => {
    let pageNo = null;
    try {
      pageNo = Number(new URL(href(a)).searchParams.get('page') || 1);
    } catch (_) {}
    push('fs_pagination_click', { page_to: pageNo, direction: a.getAttribute('rel') || 'number', list_page_type: page.page_type });
  });
  on('click', '.breadcrumb a[href]', (a) => push('fs_breadcrumb_click', { link_text: text(a), link_url: href(a) }));

  const SOCIAL = [
    ['instagram.com', 'instagram'],
    ['facebook.com', 'facebook'],
    ['twitter.com', 'x'],
    ['x.com', 'x'],
    ['line.me', 'line'],
    ['lin.ee', 'line'],
    ['youtube.com', 'youtube'],
    ['tiktok.com', 'tiktok'],
    ['pinterest.', 'pinterest'],
  ];
  on('click', 'a[href]', (a) => {
    const raw = a.getAttribute('href') || '';
    if (/^(tel|mailto):/i.test(raw)) {
      push('fs_contact_link_click', { link_type: raw.split(':')[0].toLowerCase(), link_location: sectionKey(a) });
      return;
    }
    if (a.closest('.share-buttons')) return;
    let url;
    try {
      url = new URL(raw, location.href);
    } catch (_) {
      return;
    }
    if (!/^https?:$/.test(url.protocol) || url.hostname === location.hostname) return;
    const social = SOCIAL.find(([d]) => url.hostname.includes(d));
    if (social) {
      push('fs_social_click', { network: social[1], link_url: url.href, link_location: a.closest('.footer') ? 'footer' : sectionKey(a) });
      return;
    }
    // 外部サイト（予約サイト・グランピング施設・パートナー等）への遷移
    push('fs_outbound_click', { link_domain: url.hostname, link_url: url.href, link_text: text(a), link_location: sectionKey(a) });
  });

  // スクロール深度（25 / 50 / 75 / 90 / 100%。ページごとに 1 回ずつ）
  const DEPTHS = [25, 50, 75, 90, 100];
  const reached = new Set();
  let ticking = false;
  const checkScroll = () => {
    ticking = false;
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    const pct = Math.min(100, Math.round((window.scrollY / max) * 100));
    DEPTHS.forEach((d) => {
      if (pct >= d && !reached.has(d)) {
        reached.add(d);
        push('fs_scroll_depth', { percent_scrolled: d });
      }
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(checkScroll);
    },
    { passive: true }
  );

  // ---- 会員・リード ----------------------------------------------------------
  const AUTH_KEY = 'fs_auth_attempt';
  const remember = (value) => {
    try {
      sessionStorage.setItem(AUTH_KEY, value);
    } catch (_) {}
  };
  on('submit', '#customer_login, form[action*="/account/login"]', () => {
    push('fs_login', { method: 'email' });
    remember('login');
  });
  on('submit', '#create_customer, form[action$="/account"][id*="create"]', () => {
    push('fs_sign_up', { method: 'email' });
    remember('sign_up');
  });
  on('submit', '#recover_customer_password, form[action*="/account/recover"]', () => push('fs_password_recover'));
  try {
    const attempt = sessionStorage.getItem(AUTH_KEY);
    if (attempt) {
      if (page.customer_logged_in) {
        push(attempt === 'sign_up' ? 'fs_sign_up_success' : 'fs_login_success', { method: 'email', customer_tier: page.customer_tier || '' });
        sessionStorage.removeItem(AUTH_KEY);
      } else if (!/\/account\/(login|register)/.test(location.pathname)) {
        sessionStorage.removeItem(AUTH_KEY); // ログインページ以外に移動して未ログインなら失敗扱いで破棄
      }
    }
  } catch (_) {}

  const formLocation = (form) =>
    form.closest('newsletter-popup') ? 'popup' : form.closest('.footer') ? 'footer' : sectionKey(form);
  on('submit', 'form', (form) => {
    const tag = form.querySelector('input[name="contact[tags]"]');
    if (tag && /newsletter/i.test(tag.value)) {
      push('fs_newsletter_signup', { method: formLocation(form) });
      return;
    }
    const type = form.querySelector('input[name="form_type"]');
    if (type && type.value === 'contact') push('fs_contact_submit', { form_location: formLocation(form), page_handle: page.page_handle || '' });
  });
  // Klaviyo のフォーム（埋め込み・ポップアップ）
  window.addEventListener('klaviyoForms', (e) => {
    if (e.detail && e.detail.type === 'submit') push('fs_newsletter_signup', { method: 'klaviyo', form_id: e.detail.formId || '' });
  });
  // 送信完了後のリダイレクト（?customer_posted=true / ?contact_posted=true）
  try {
    const params = new URLSearchParams(location.search);
    if (params.get('customer_posted') === 'true') push('fs_newsletter_signup_success');
    if (params.get('contact_posted') === 'true') push('fs_contact_success', { page_handle: page.page_handle || '' });
  } catch (_) {}

  // ---- DOM 変化の監視（AJAX 差し替え・遅延描画）--------------------------------
  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(observePromotions, 300);
  };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();
})();
